/**
 * Central de Execução Operacional — tela inicial do módulo Tarefas.
 */
import React, { useEffect, useMemo } from 'react';
import {
  Activity, AlertTriangle, CheckCircle2, Clock, History,
  MessageSquare, Paperclip, Play, Workflow,
} from 'lucide-react';
import type { TaskListItem } from '../../shared/types/task';
import type { UnifiedWorkItem } from '../types/workItem';
import type { MyTasksSummary } from '../../../services/workflowService';
import {
  formatStatusLabel,
  resolveUrgency,
} from '../utils/taskDisplayUtils';
import {
  auditExecutionCenterCoherence,
  buildExecutionCenterKpis,
  buildFifoQueue,
  buildNearSlaItems,
  buildOperationalContextContent,
  buildOverdueSlaItems,
  buildAttentionItems,
  buildBlockerItems,
  buildShiftLoadForecast,
  countOverdueTasks,
  filterAttentionExcluding,
  formatRemainingMinutes,
  formatShiftMinutes,
  formatTaskSlaDetail,
  isTaskCriticallyAttentioned,
  logNextExecutionSelection,
  resolveNextExecutableTask,
  resolveOperationalPriority,
} from '../utils/executionCenterUtils';
import type { OperationalContextContent } from '../utils/executionCenterUtils';

interface OperationalExecutionCenterProps {
  userName: string;
  claimedTasks: TaskListItem[];
  queueTasks: TaskListItem[];
  inboxRailTasks: TaskListItem[];
  unifiedWorkItems: UnifiedWorkItem[];
  kpis: MyTasksSummary | null;
  avgDurationMin: number;
  onSelectTask: (taskId: string) => void;
  onPullNext?: () => void;
  checkWip: (stageId: string, stageName: string, all: TaskListItem[]) => { exceeded: boolean; current: number; limit: number };
}

function formatCode(task: TaskListItem): string {
  return task.referenceCode ?? task.instanceTitle ?? task.instanceId.slice(0, 8).toUpperCase();
}

function FocusExecutivePanel({
  task,
  showCriticalBadge,
  onExecute,
  actionLabel,
  queueCount,
  onPullNext,
  showPullNext,
}: {
  task: TaskListItem;
  showCriticalBadge?: boolean;
  onExecute: () => void;
  actionLabel: string;
  queueCount: number;
  onPullNext?: () => void;
  showPullNext?: boolean;
}) {
  const urgency = resolveUrgency(task);
  const slaDetail = formatTaskSlaDetail(task);
  const isOverdue = urgency.level === 'overdue';
  const priority = resolveOperationalPriority(task);
  const title = task.stageName ?? task.instanceTitle ?? 'Atividade';
  const isCritical = showCriticalBadge || priority.operationalLabel === 'Crítica';

  return (
    <section className="relative shrink-0 overflow-hidden rounded-md border border-slate-200/90 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900">
      <div className={`absolute inset-y-0 left-0 w-1 ${isCritical ? 'bg-red-500' : 'bg-blue-600'}`} />
      <div className="flex flex-col gap-3 p-4 pl-5 lg:flex-row lg:items-center lg:justify-between">
        <div className="min-w-0 flex-1">
          <p className="text-[9px] font-bold uppercase tracking-[0.16em] text-slate-400">Foco agora · prioridade operacional</p>
          <h2 className="mt-1 text-[26px] font-bold leading-[1.15] tracking-tight text-slate-900 dark:text-white sm:text-[30px]">
            {title}
          </h2>
          <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
            {isCritical && (
              <span className="rounded bg-red-600 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white">
                Crítica
              </span>
            )}
            <span className={`rounded px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide ${
              isOverdue
                ? 'bg-red-100 text-red-700 dark:bg-red-950/50 dark:text-red-300'
                : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'
            }`}>
              SLA {slaDetail}
            </span>
            <span className="rounded bg-blue-600/10 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-blue-700 dark:bg-blue-950/40 dark:text-blue-300">
              {formatStatusLabel(task.status)}
            </span>
          </div>
          <p className="mt-2 truncate text-[10px] text-slate-400">
            {task.instanceTitle}
            <span className="mx-1 text-slate-300">·</span>
            <span className="font-mono">{formatCode(task)}</span>
            <span className="mx-1 text-slate-300">·</span>
            {priority.operationalLabel}
          </p>
        </div>
        <div className="flex shrink-0 flex-col items-stretch gap-1 lg:min-w-[200px] lg:items-end">
          <button
            type="button"
            onClick={onExecute}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-blue-600 px-7 py-3 text-[12px] font-bold uppercase tracking-[0.08em] text-white shadow-md transition hover:bg-blue-700"
          >
            <Play size={16} fill="currentColor" />
            {actionLabel}
          </button>
          <span className="text-center text-[8px] font-medium uppercase tracking-wide text-slate-400 lg:text-right">
            +{queueCount} na fila operacional
          </span>
          {showPullNext && onPullNext && (
            <button type="button" onClick={onPullNext} className="text-[10px] font-semibold text-blue-600 hover:underline dark:text-blue-400">
              Assumir da fila de papel
            </button>
          )}
        </div>
      </div>
    </section>
  );
}

interface OperationalSummaryProps {
  openDemands: number;
  inProgressCount: number;
  lateOrBlocked: number;
}

function OperationalSummary({
  openDemands,
  inProgressCount,
  lateOrBlocked,
}: OperationalSummaryProps) {
  const metrics = [
    {
      label: 'Em execução',
      value: inProgressCount,
      tone: inProgressCount > 0 ? 'text-blue-600 dark:text-blue-400' : 'text-slate-800 dark:text-slate-100',
      dot: inProgressCount > 0 ? 'bg-blue-500' : 'bg-slate-300 dark:bg-slate-600',
    },
    {
      label: 'Atrasadas ou bloqueadas',
      value: lateOrBlocked,
      tone: lateOrBlocked > 0 ? 'text-red-600 dark:text-red-400' : 'text-slate-800 dark:text-slate-100',
      dot: lateOrBlocked > 0 ? 'bg-red-500' : 'bg-slate-300 dark:bg-slate-600',
    },
  ] as const;

  return (
    <section className="rounded-lg border border-slate-200/90 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900">
      <div className="flex flex-col lg:flex-row lg:items-stretch">
        <div className="flex shrink-0 items-center gap-3 border-b border-slate-100 px-4 py-3 lg:border-b-0 lg:border-r lg:py-3.5 dark:border-slate-800">
          <span className="text-[40px] font-bold tabular-nums leading-none tracking-tight text-slate-900 dark:text-white">
            {openDemands}
          </span>
          <div>
            <p className="text-[11px] font-semibold text-slate-800 dark:text-slate-100">Demandas abertas</p>
            <p className="text-[10px] text-slate-500 dark:text-slate-400">Carteira unificada</p>
          </div>
        </div>

        <div className="grid flex-1 grid-cols-2 divide-x divide-slate-100 dark:divide-slate-800">
          {metrics.map((m) => (
            <div key={m.label} className="flex flex-col justify-center px-3 py-2.5 sm:px-4 sm:py-3">
              <div className="flex items-center gap-1.5">
                <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${m.dot}`} />
                <span className="text-[10px] font-medium text-slate-500 dark:text-slate-400">{m.label}</span>
              </div>
              <div
                className={`mt-1 text-[20px] font-bold tabular-nums leading-none sm:text-[22px] ${m.tone} ${
                  'isText' in m && m.isText && String(m.value).length > 6 ? 'text-[15px] sm:text-[16px]' : ''
                }`}
              >
                {m.value}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

interface HealthMonitorProps {
  shiftLoad: ReturnType<typeof buildShiftLoadForecast>;
  loadBarTone: string;
  nearSlaItems: ReturnType<typeof buildNearSlaItems>;
  overdueSlaItems: ReturnType<typeof buildOverdueSlaItems>;
  blockers: ReturnType<typeof buildBlockerItems>;
  onSelectTask: (id: string) => void;
  hasActiveExecution: boolean;
}

function HealthMonitor({
  shiftLoad,
  loadBarTone,
  nearSlaItems,
  overdueSlaItems,
  blockers,
  onSelectTask,
  hasActiveExecution,
}: HealthMonitorProps) {
  const alertCount = nearSlaItems.length + overdueSlaItems.length + blockers.length;
  const overallOk = alertCount === 0 && shiftLoad.tone === 'ok';
  const overallTone = overallOk ? 'emerald' : alertCount > 2 || shiftLoad.tone === 'critical' ? 'red' : 'amber';

  const toneBorder = {
    emerald: 'border-emerald-200 dark:border-emerald-900/40',
    amber: 'border-amber-200 dark:border-amber-900/40',
    red: 'border-red-200 dark:border-red-900/40',
  }[overallTone];

  const toneDot = {
    emerald: 'bg-emerald-500',
    amber: 'bg-amber-500',
    red: 'bg-red-500',
  }[overallTone];

  const operationState =
    alertCount > 0
      ? 'Atenção necessária em itens com SLA ou bloqueios'
      : shiftLoad.tone === 'warning'
        ? 'Operação sob pressão de capacidade'
        : hasActiveExecution
          ? 'Execução em andamento'
          : 'Operação estável';

  return (
    <aside className={`flex h-full min-h-0 flex-col rounded-lg border bg-white dark:bg-slate-900 ${toneBorder}`}>
      <header className="flex items-center justify-between gap-2 border-b border-slate-100 px-3 py-2 dark:border-slate-800">
        <div className="flex items-center gap-2">
          <span className={`h-2 w-2 rounded-full ${toneDot}`} />
          <span className="text-[10px] font-semibold text-slate-700 dark:text-slate-200">Saúde da operação</span>
        </div>
        <span className="text-[10px] font-bold text-slate-500">
          {overallOk ? 'Estável' : 'Monitorar'}
        </span>
      </header>

      <div className="border-b border-slate-100 px-3 py-2 dark:border-slate-800">
        <p className="text-[10px] font-medium text-slate-700 dark:text-slate-200">{operationState}</p>
        <p className="mt-0.5 text-[9px] leading-snug text-slate-500">{shiftLoad.message}</p>
      </div>

      <div className="border-b border-slate-100 px-3 py-2 dark:border-slate-800">
        <div className="mb-1 flex items-center justify-between">
          <span className="text-[9px] font-bold uppercase tracking-wide text-slate-500">Capacidade do turno</span>
          <span className={`text-[9px] font-semibold ${
            shiftLoad.tone === 'critical' ? 'text-red-600' : shiftLoad.tone === 'warning' ? 'text-amber-600' : 'text-emerald-600'
          }`}>
            {shiftLoad.riskLabel}
          </span>
        </div>
        <div className="h-2 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
          <div
            className={`h-full rounded-full transition-all ${loadBarTone}`}
            style={{ width: `${Math.max(shiftLoad.utilizationPct, shiftLoad.utilizationPct > 0 || hasActiveExecution ? 4 : 0)}%` }}
          />
        </div>
        {shiftLoad.estimatedEndLabel && (
          <p className="mt-1 text-[9px] text-slate-500">
            Previsão de término: <span className="font-medium text-slate-700 dark:text-slate-300">{shiftLoad.estimatedEndLabel}</span>
          </p>
        )}
        {shiftLoad.waitingCount > 0 && (
          <p className="mt-0.5 text-[9px] text-slate-500">
            {shiftLoad.waitingCount} demanda{shiftLoad.waitingCount !== 1 ? 's' : ''} aguardando · ~{formatShiftMinutes(shiftLoad.estimatedMinutes)}
          </p>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {overdueSlaItems.length > 0 && (
          <div className="border-b border-slate-100 px-3 py-2 dark:border-slate-800">
            <p className="mb-1.5 text-[9px] font-bold uppercase tracking-wide text-red-600">SLA vencido</p>
            <ul className="space-y-0.5">
              {overdueSlaItems.slice(0, 8).map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => onSelectTask(item.taskId)}
                    className="flex w-full items-center gap-2 rounded px-1 py-1 text-left hover:bg-red-50 dark:hover:bg-red-950/20"
                  >
                    <span className="min-w-0 flex-1 truncate text-[10px] font-medium text-slate-700 dark:text-slate-200">{item.title}</span>
                    <span className="shrink-0 text-[8px] font-bold text-red-600">Vencido</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {nearSlaItems.length > 0 && (
          <div className="border-b border-slate-100 px-3 py-2 dark:border-slate-800">
            <p className="mb-1.5 text-[9px] font-bold uppercase tracking-wide text-amber-600">SLA próximo do vencimento</p>
            <ul className="space-y-0.5">
              {nearSlaItems.slice(0, 8).map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => onSelectTask(item.taskId)}
                    className="flex w-full items-center gap-2 rounded px-1 py-1 text-left hover:bg-amber-50 dark:hover:bg-amber-950/20"
                  >
                    <span className="shrink-0 text-[8px] font-bold text-amber-700">{formatRemainingMinutes(item.remainingMinutes)}</span>
                    <span className="min-w-0 flex-1 truncate text-[10px] font-medium text-slate-700 dark:text-slate-200">{item.title}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {blockers.length > 0 && (
          <div className="px-3 py-2">
            <p className="mb-1.5 text-[9px] font-bold uppercase tracking-wide text-amber-700">Bloqueios ativos</p>
            <ul className="space-y-0.5">
              {blockers.slice(0, 8).map((blocker) => (
                <li key={blocker.id}>
                  {blocker.taskId ? (
                    <button
                      type="button"
                      onClick={() => onSelectTask(blocker.taskId!)}
                      className="flex w-full items-start gap-2 rounded px-1 py-1 text-left hover:bg-slate-50 dark:hover:bg-slate-800/50"
                    >
                      <span className="min-w-0 flex-1 text-[10px] font-medium text-slate-700 dark:text-slate-200">{blocker.label}</span>
                    </button>
                  ) : (
                    <p className="px-1 text-[10px] font-medium text-red-700">{blocker.label}</p>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {alertCount === 0 && (
          <p className="flex items-center gap-1.5 px-3 py-3 text-[10px] text-emerald-600 dark:text-emerald-400">
            <CheckCircle2 size={12} className="shrink-0" />
            Sem alertas de SLA ou bloqueios ativos
          </p>
        )}
      </div>
    </aside>
  );
}

function ContextPanel({
  title,
  subtitle,
  icon: Icon,
  children,
}: {
  title: string;
  subtitle?: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  children: React.ReactNode;
}) {
  return (
    <section className="flex min-h-0 flex-col rounded-md border border-slate-200/90 bg-white dark:border-slate-700 dark:bg-slate-900">
      <header className="shrink-0 border-b border-slate-100 px-3 py-2 dark:border-slate-800">
        <div className="flex items-center gap-2">
          <Icon size={12} className="shrink-0 text-slate-500" />
          <div className="min-w-0">
            <p className="text-[9px] font-bold uppercase tracking-[0.12em] text-slate-600 dark:text-slate-300">{title}</p>
            {subtitle && <p className="text-[8px] text-slate-400">{subtitle}</p>}
          </div>
        </div>
      </header>
      <div className="min-h-0 flex-1 overflow-y-auto p-2">{children}</div>
    </section>
  );
}

const TIMELINE_TONE_CLASS = {
  default: 'bg-slate-300',
  ok: 'bg-emerald-500',
  info: 'bg-blue-500',
  warning: 'bg-amber-500',
};

function OperationalContextStrip({
  context,
  onSelectTask,
  className,
}: {
  context: OperationalContextContent;
  onSelectTask: (id: string) => void;
  className?: string;
}) {
  return (
    <section className={`flex min-h-[180px] flex-col rounded-md border border-slate-200/90 bg-white dark:border-slate-700 dark:bg-slate-900 ${className ?? ''}`}>
      <header className="shrink-0 border-b border-slate-100 px-3 py-2 dark:border-slate-800">
        <div className="flex items-center gap-2">
          <Activity size={12} className="text-slate-500" />
          <span className="text-[9px] font-bold uppercase tracking-[0.12em] text-slate-600 dark:text-slate-300">
            Contexto operacional
          </span>
        </div>
      </header>
      <div className="grid min-h-0 flex-1 grid-cols-1 gap-2 overflow-y-auto p-2 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7">
        <ContextPanel title="Últimas execuções" subtitle="Conclusões recentes" icon={CheckCircle2}>
          {context.recentExecutions.length === 0 ? (
            <p className="text-[10px] text-slate-500">Sem conclusões recentes.</p>
          ) : (
            <ul className="space-y-1">
              {context.recentExecutions.map((item) => (
                <li key={item.id} className="text-[10px]">
                  <p className="truncate font-medium text-slate-800 dark:text-slate-100">{item.title}</p>
                  <p className="text-[9px] text-slate-400">{item.whenLabel} · {item.sourceLabel}</p>
                </li>
              ))}
            </ul>
          )}
        </ContextPanel>

        <ContextPanel title="Últimos comentários" icon={MessageSquare}>
          {context.recentComments.length === 0 ? (
            <p className="text-[10px] text-slate-500">Sem observações registradas.</p>
          ) : (
            <ul className="space-y-1">
              {context.recentComments.map((item) => (
                <li key={item.id}>
                  <button type="button" onClick={() => onSelectTask(item.taskId)} className="w-full text-left hover:opacity-80">
                    <p className="truncate text-[10px] font-medium text-slate-800 dark:text-slate-100">{item.taskTitle}</p>
                    <p className="line-clamp-2 text-[9px] text-slate-500">{item.excerpt}</p>
                    <p className="text-[8px] text-slate-400">{item.whenLabel}</p>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </ContextPanel>

        <ContextPanel title="Últimos anexos" icon={Paperclip}>
          {context.recentAttachments.length === 0 ? (
            <p className="text-[10px] text-slate-500">Sem anexos na carteira atual.</p>
          ) : (
            <ul className="space-y-1">
              {context.recentAttachments.map((item) => (
                <li key={item.id}>
                  <button type="button" onClick={() => onSelectTask(item.taskId)} className="w-full text-left hover:opacity-80">
                    <p className="truncate text-[10px] font-medium text-slate-800 dark:text-slate-100">{item.fileName}</p>
                    <p className="text-[9px] text-slate-400">{item.taskTitle} · {item.whenLabel}</p>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </ContextPanel>

        <ContextPanel title="Histórico da atividade" subtitle="Foco atual" icon={History}>
          {context.focusActivityHistory.length === 0 ? (
            <p className="text-[10px] text-slate-500">Sem atividade em foco.</p>
          ) : (
            <ol className="space-y-1">
              {context.focusActivityHistory.map((item) => (
                <li key={item.id} className="flex gap-2">
                  <span className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${TIMELINE_TONE_CLASS[item.tone ?? 'default']}`} />
                  <div className="min-w-0">
                    <p className="text-[10px] font-medium text-slate-800 dark:text-slate-100">{item.title}</p>
                    {item.detail && <p className="text-[9px] text-slate-500">{item.detail}</p>}
                    <p className="text-[8px] text-slate-400">{item.whenLabel}</p>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </ContextPanel>

        <ContextPanel title="Eventos recentes" icon={Clock}>
          {context.recentEvents.length === 0 ? (
            <p className="text-[10px] text-slate-500">Sem eventos operacionais.</p>
          ) : (
            <ul className="space-y-1">
              {context.recentEvents.map((item) => (
                <li key={item.id}>
                  <button type="button" onClick={() => onSelectTask(item.taskId)} className="w-full text-left hover:opacity-80">
                    <p className="truncate text-[10px] font-medium text-slate-800 dark:text-slate-100">{item.eventLabel}</p>
                    <p className="text-[9px] text-slate-400">{item.taskTitle} · {item.whenLabel}</p>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </ContextPanel>

        <ContextPanel title="Problemas recentes" icon={AlertTriangle}>
          {context.recentProblems.length === 0 ? (
            <p className="text-[10px] text-slate-500">Sem problemas abertos.</p>
          ) : (
            <ul className="space-y-1">
              {context.recentProblems.map((item) => (
                <li key={item.id} className="text-[10px]">
                  <p className="truncate font-medium text-slate-800 dark:text-slate-100">{item.title}</p>
                  <p className="text-[9px] text-slate-400">
                    {item.statusLabel}
                    {item.severityLabel && <><span className="mx-1">·</span>{item.severityLabel}</>}
                    <span className="mx-1">·</span>
                    {item.whenLabel}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </ContextPanel>

        <ContextPanel title="Timeline operacional" icon={Workflow}>
          {context.operationalTimeline.length === 0 ? (
            <p className="text-[10px] text-slate-500">Sem movimentações recentes.</p>
          ) : (
            <ol className="space-y-1">
              {context.operationalTimeline.map((item) => (
                <li key={item.id} className="flex gap-2">
                  <span className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${TIMELINE_TONE_CLASS[item.tone ?? 'default']}`} />
                  <div className="min-w-0">
                    <p className="truncate text-[10px] font-medium text-slate-800 dark:text-slate-100">{item.title}</p>
                    {item.detail && <p className="line-clamp-2 text-[9px] text-slate-500">{item.detail}</p>}
                    <p className="text-[8px] text-slate-400">{item.whenLabel}</p>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </ContextPanel>
      </div>
    </section>
  );
}

export const OperationalExecutionCenter: React.FC<OperationalExecutionCenterProps> = ({
  userName,
  claimedTasks,
  queueTasks,
  inboxRailTasks,
  unifiedWorkItems,
  kpis,
  avgDurationMin,
  onSelectTask,
  onPullNext,
  checkWip,
}) => {
  const nextSelection = useMemo(
    () => resolveNextExecutableTask(claimedTasks, queueTasks, inboxRailTasks),
    [claimedTasks, queueTasks, inboxRailTasks],
  );
  const nextTask = nextSelection.task;
  const fifoQueue = useMemo(
    () => buildFifoQueue(claimedTasks, queueTasks, 8),
    [claimedTasks, queueTasks],
  );
  const waitingQueue = useMemo(
    () => (nextTask ? fifoQueue.filter(t => t.id !== nextTask.id) : fifoQueue),
    [fifoQueue, nextTask],
  );
  const allAttentionItems = useMemo(
    () => buildAttentionItems(claimedTasks, queueTasks, unifiedWorkItems),
    [claimedTasks, queueTasks, unifiedWorkItems],
  );
  const attentionItems = useMemo(
    () => filterAttentionExcluding(allAttentionItems, nextTask?.id),
    [allAttentionItems, nextTask?.id],
  );
  const topAttention = attentionItems[0] ?? null;
  const criticalCount = attentionItems.filter(i => i.tone === 'critical').length;
  const nextTaskIsCritical = nextTask ? isTaskCriticallyAttentioned(nextTask) : false;
  const nearSlaItems = useMemo(
    () => buildNearSlaItems(claimedTasks, queueTasks),
    [claimedTasks, queueTasks],
  );
  const overdueSlaItems = useMemo(
    () => buildOverdueSlaItems(claimedTasks, queueTasks, unifiedWorkItems),
    [claimedTasks, queueTasks, unifiedWorkItems],
  );
  const blockers = useMemo(
    () => buildBlockerItems(claimedTasks, queueTasks, checkWip),
    [claimedTasks, queueTasks, checkWip],
  );

  const centerKpis = useMemo(
    () =>
      buildExecutionCenterKpis({
        claimedTasks,
        queueTasks,
        inboxRailTasks,
        unifiedWorkItems,
        attentionItems: allAttentionItems,
        blockers,
        avgDurationMin,
        nextTaskId: nextTask?.id,
        slaRate7d: kpis?.my_sla_rate_7d,
      }),
    [
      claimedTasks,
      queueTasks,
      inboxRailTasks,
      unifiedWorkItems,
      allAttentionItems,
      blockers,
      avgDurationMin,
      nextTask?.id,
      kpis?.my_sla_rate_7d,
    ],
  );

  const shiftLoad = centerKpis.shiftLoad;
  const overdueCount = kpis?.my_overdue_count ?? countOverdueTasks(claimedTasks, queueTasks, unifiedWorkItems);
  const isStable = centerKpis.openDemands === 0 && !nextTask && waitingQueue.length === 0;

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Bom dia' : hour < 18 ? 'Boa tarde' : 'Boa noite';
  const firstName = userName.split(' ')[0] || 'Operador';

  const nextActionLabel =
    nextTask?.status === 'in_progress' ? 'Continuar execução'
    : nextTask?.status === 'paused' ? 'Retomar tarefa'
    : nextTask?.isRoleQueue ? 'Assumir e executar'
    : 'Executar agora';

  const loadBarTone =
    shiftLoad.tone === 'critical' ? 'bg-red-500' : shiftLoad.tone === 'warning' ? 'bg-amber-500' : 'bg-emerald-500';

  useEffect(() => {
    logNextExecutionSelection(nextSelection);
    auditExecutionCenterCoherence({
      nextTask: nextSelection.task,
      inProgressCount: centerKpis.inProgress,
      overdueCount,
      overdueSlaCount: overdueSlaItems.length,
      attentionCount: attentionItems.length,
      waitingQueueCount: waitingQueue.length,
      shiftUtilizationPct: shiftLoad.utilizationPct,
      blockerCount: blockers.length,
      openDemandsCount: centerKpis.openDemands,
    });
  }, [
    nextSelection,
    centerKpis.inProgress,
    centerKpis.openDemands,
    overdueCount,
    overdueSlaItems.length,
    attentionItems.length,
    waitingQueue.length,
    shiftLoad.utilizationPct,
    blockers.length,
  ]);

  const lateOrBlocked = useMemo(() => {
    const ids = new Set<string>();
    for (const item of overdueSlaItems) ids.add(item.taskId);
    for (const item of blockers) ids.add(item.taskId ?? item.id);
    return ids.size;
  }, [overdueSlaItems, blockers]);

  const summaryProps = {
    openDemands: centerKpis.openDemands,
    inProgressCount: centerKpis.inProgress,
    lateOrBlocked,
  };

  const operationalContext = useMemo(
    () =>
      buildOperationalContextContent({
        claimedTasks,
        queueTasks,
        inboxRailTasks,
        unifiedWorkItems,
        nextTask,
      }),
    [claimedTasks, queueTasks, inboxRailTasks, unifiedWorkItems, nextTask],
  );

  const showOperationalContext = operationalContext.recentComments.length > 0
    || operationalContext.recentAttachments.length > 0
    || operationalContext.recentProblems.length > 0;

  const healthMonitorProps = {
    shiftLoad,
    loadBarTone,
    nearSlaItems,
    overdueSlaItems,
    blockers,
    onSelectTask,
    hasActiveExecution: centerKpis.inProgress > 0,
  };

  const workspaceGrid = (
    <div className={`grid min-h-0 flex-1 grid-cols-1 gap-2 xl:grid-cols-[minmax(0,1fr)_280px] ${showOperationalContext ? 'xl:grid-rows-[minmax(0,1fr)_auto]' : ''}`}>
      <div className="flex min-h-0 flex-col gap-2">
        {!isStable && (
          nextTask ? (
            <FocusExecutivePanel
              task={nextTask}
              showCriticalBadge={nextTaskIsCritical}
              onExecute={() => onSelectTask(nextTask.id)}
              actionLabel={nextActionLabel}
              queueCount={waitingQueue.length}
              onPullNext={onPullNext}
              showPullNext={Boolean(nextTask.isRoleQueue && onPullNext)}
            />
          ) : (
            <section className="shrink-0 rounded-md border border-dashed border-slate-200 bg-white/60 px-3 py-2.5 dark:border-slate-700 dark:bg-slate-900/40">
              <p className="text-[9px] font-bold uppercase tracking-wide text-slate-500">Foco agora</p>
              <p className="mt-0.5 text-[11px] text-slate-600 dark:text-slate-400">
                Sem workflow ativo — selecione na carteira ou aguarde novos itens.
              </p>
            </section>
          )
        )}

        {!isStable && attentionItems.length > 0 && (
          <section className={`shrink-0 overflow-hidden rounded-md border ${
            topAttention?.tone === 'critical'
              ? 'border-red-200 bg-red-50/50 dark:border-red-900/40 dark:bg-red-950/15'
              : 'border-amber-200 bg-amber-50/50 dark:border-amber-900/40 dark:bg-amber-950/15'
          }`}>
            <header className="flex items-center gap-2 px-3 py-1.5">
              <AlertTriangle size={12} className={topAttention?.tone === 'critical' ? 'text-red-600' : 'text-amber-600'} />
              <span className="text-[9px] font-bold uppercase tracking-wide text-slate-700 dark:text-slate-200">
                Atenção imediata
              </span>
            </header>
            {topAttention && (
              <div className="flex flex-col gap-2 border-t border-inherit px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <p className={`text-[10px] font-bold ${topAttention.tone === 'critical' ? 'text-red-700 dark:text-red-400' : 'text-amber-700 dark:text-amber-400'}`}>
                    {topAttention.tone === 'critical'
                      ? `${criticalCount} outra${criticalCount !== 1 ? 's' : ''} demanda${criticalCount !== 1 ? 's' : ''} crítica${criticalCount !== 1 ? 's' : ''}`
                      : topAttention.reason}
                  </p>
                  <p className="mt-0.5 truncate text-[12px] font-semibold text-slate-900 dark:text-slate-50">{topAttention.title}</p>
                  <p className="text-[9px] text-slate-500">{topAttention.slaDetail ?? topAttention.reason}</p>
                </div>
                <button
                  type="button"
                  onClick={() => onSelectTask(topAttention.taskId)}
                  className="shrink-0 rounded border border-slate-300 bg-white px-3 py-1.5 text-[10px] font-bold uppercase tracking-wide text-slate-700 hover:border-blue-500 hover:text-blue-700 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
                >
                  Ver detalhes
                </button>
              </div>
            )}
          </section>
        )}

      </div>

      <div className="min-h-0">
        <HealthMonitor {...healthMonitorProps} />
      </div>

      {showOperationalContext && (
        <OperationalContextStrip
          context={operationalContext}
          onSelectTask={onSelectTask}
          className="xl:col-span-2"
        />
      )}
    </div>
  );

  if (isStable) {
    return (
      <div className="flex h-full min-h-0 w-full flex-col gap-2 overflow-hidden bg-slate-50 px-2 py-2 dark:bg-slate-950">
        <OperationalSummary {...summaryProps} />
        <div className="flex shrink-0 items-center gap-3 rounded-md bg-emerald-50 px-4 py-2.5 dark:bg-emerald-950/20">
          <CheckCircle2 size={20} className="shrink-0 text-emerald-600 dark:text-emerald-400" />
          <div>
            <p className="text-[13px] font-bold text-slate-800 dark:text-slate-100">{greeting}, {firstName}</p>
            <p className="text-[11px] text-slate-600 dark:text-slate-400">Carteira vazia · Operação estável</p>
          </div>
        </div>
        {workspaceGrid}
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 w-full flex-col gap-2 overflow-hidden bg-slate-50 px-2 py-2 dark:bg-slate-950">
      <OperationalSummary {...summaryProps} />
      {workspaceGrid}
    </div>
  );
};

export default OperationalExecutionCenter;
